import { useContext, useState, useEffect, useCallback, useRef } from 'react';
import TenantContext from '../../contexts/tenantContext';
import ResponsiveBox, { Row, Col, Item, Location } from 'devextreme-react/responsive-box';
import notify from 'devextreme/ui/notify';

import './CreazioneFiltri.css';
import FiltriTenant from "../../components/FilterBuilderChild/FiltriTenant";
import Filtro from "../../components/FilterBuilderChild/Filtro";
import AnteprimaFiltro from "../../components/FilterBuilderChild/AnteprimaFiltro";
import { useFiltriDataSource, useFilterBuilderFieldsDataSource, useAnteprimaDataSource } from '../../datasources/FilterBuilderDataSource';

// Interfaccia per il filtro selezionato
interface SelectedFilter {
    filtroId: number;
    nome: string;
    espressione: any;
}

export default function CreazioneFiltri() {
    const tenantContext = useContext(TenantContext);
    
    // Stato condiviso tra i componenti
    const [selectedFilter, setSelectedFilter] = useState<SelectedFilter | null>(null);
    const [filterValue, setFilterValue] = useState<any[]>([]);
    const [filterName, setFilterName] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [fields, setFields] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [lastSavedFilterId, setLastSavedFilterId] = useState<number | null>(null);
    
    // Forza l'aggiornamento quando cambia il lastSavedFilterId
    const [refreshCounter, setRefreshCounter] = useState(0);
    
    // Riferimento alla griglia FiltriTenant per forzare il refresh
    const filtriTenantGridRef = useRef<any>(null);
    
    // DataSources
    const filtriTenantDataSource = useFiltriDataSource(refreshCounter);
    
    // Forza un refresh del DataGrid quando lastSavedFilterId cambia
    useEffect(() => {
        if (lastSavedFilterId !== null) {
            const refreshGrid = async () => {
                //console.log('Nuovo filtro salvato con ID:', lastSavedFilterId);
            };
            
            refreshGrid();
        }
    }, [lastSavedFilterId, filtriTenantDataSource]);
    
    const fieldsDataSource = useFilterBuilderFieldsDataSource();
    
    // Carica i campi per il FilterBuilder all'avvio
    useEffect(() => {
        setIsLoading(true);
        fieldsDataSource.load()
            .then(loadedFields => {
                const validFields = Array.isArray(loadedFields) ? loadedFields : [];
                // console.log('Campi caricati:', validFields);
                setFields(validFields);
                setIsLoading(false);
            })
            .catch(error => {
                console.error('Errore nel caricamento dei campi:', error);
                setFields([]);
                setIsLoading(false);
            });
    }, [fieldsDataSource]);
    
    // Calcola l'espressione di filtro come stringa per l'anteprima
    const filterExpression = useCallback(() => {
        if (!filterValue || filterValue.length === 0) return '';
        return typeof filterValue === 'string' ? filterValue : JSON.stringify(filterValue);
    }, [filterValue]);
    
    // DataSource per l'anteprima che si aggiorna quando cambia il filtro
    const anteprimaDataSource = useAnteprimaDataSource(filterExpression());
    
    // Handler per la selezione di un filtro da FiltriTenant
    const handleFilterSelect = useCallback((filter: SelectedFilter) => {
        if (!filter) {
            console.warn('Selezionato un filtro nullo o undefined');
            return;
        }
        
        // console.log('Filtro selezionato:', filter);
        setSelectedFilter(filter);
        
        // Se filter.espressione è una stringa (JSON), la convertiamo in oggetto
        let parsedExpression = filter.espressione;
        if (typeof filter.espressione === 'string') {
            try {
                parsedExpression = JSON.parse(filter.espressione);
                // console.log('Espressione parsata:', parsedExpression);
            } catch (e) {
                console.error('Errore nel parsing dell\'espressione:', e);
                // In caso di errore di parsing, usiamo un array vuoto
                parsedExpression = [];
            }
        }
        
        // Verifica che l'espressione sia un array
        if (!Array.isArray(parsedExpression)) {
            console.warn('L\'espressione non è un array, verrà convertita in un array vuoto');
            parsedExpression = [];
        }
        
        setFilterValue(parsedExpression);
        setFilterName(filter.nome || '');
        
        // Attiva automaticamente l'anteprima quando un filtro viene selezionato
        setShowPreview(true);
    }, []);


const handleFilterDelete = useCallback(async (id: number) => {
    if (id == null) {
        console.error('ID del filtro da eliminare non valido');
        return false;
    }
    
    try {
        // console.log('Eliminazione filtro con ID:', id);
        await filtriTenantDataSource.remove(id);
        
        // Forza un aggiornamento del DataGrid
        await filtriTenantDataSource.load();

        // Se il filtro correntemente selezionato è stato eliminato, resetta lo stato
        if (selectedFilter && selectedFilter.filtroId === id) {
            setSelectedFilter(null);
            setFilterValue([]);
            setFilterName('');
            setShowPreview(false);
        }
        
        // Forza un aggiornamento del contatore di refresh
        setRefreshCounter(prev => prev + 1);
        
        // Mostra un messaggio di successo
        notify({
            message: 'Filtro eliminato con successo',
            type: 'success',
            displayTime: 3000,
            position: { at: 'top center', my: 'top center' }
        });
        
        // Utilizza il metodo refreshData se disponibile (con timeout aumentato)
        if (filtriTenantGridRef.current && typeof filtriTenantGridRef.current.refreshData === 'function') {
            setTimeout(() => {
                filtriTenantGridRef.current.refreshData();
            }, 500); // Aumentato a 500ms come in handleSaveFilter
        }
        
        return true;
    } catch (error) {
        console.error('Errore durante l\'eliminazione del filtro:', error);
        
        // Mostra un messaggio di errore
        notify({
            message: 'Errore durante l\'eliminazione del filtro',
            type: 'error',
            displayTime: 3000,
            position: { at: 'top center', my: 'top center' }
        });
        
        return false;
    }
}, [filtriTenantDataSource, selectedFilter]);

    
    // Handler per il reset del filtro
    const handleFilterReset = useCallback(() => {
        // console.log('Reset filtro');
        setSelectedFilter(null);
        setFilterValue([]);
        setFilterName('');
        setShowPreview(false);
    }, []);
    
    // Handler per visualizzare l'anteprima
    const handleShowPreview = useCallback(() => {
        // console.log('Visualizza anteprima');
        setShowPreview(true);
    }, []);
    
    // Handler per gestire i cambiamenti del valore del filtro
    const handleFilterValueChanged = useCallback((value: any) => {
        // console.log('Valore filtro cambiato:', value);
        
        // verifica che il valore sia sempre un array
        if (!value) {
            // console.warn('Valore del filtro nullo o undefined, imposto array vuoto');
            setFilterValue([]);
            return;
        }
        
        // Se il valore non è un array, si converte in un array con un elemento
        if (!Array.isArray(value)) {
            // console.warn('Valore del filtro non è un array, converto:', value);
            setFilterValue([value]);
            return;
        }
        
        setFilterValue(value);
        
        //suggerisce un nome predefinito per il filtro
        if (value.length > 0 && (!filterName || filterName.trim().length === 0)) {
            const defaultName = `Filtro ${new Date().toLocaleString('it-IT', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric', 
                hour: '2-digit', 
                minute: '2-digit' 
            })}`;
            // console.log('Impostazione nome predefinito:', defaultName);
            setFilterName(defaultName);
        }
    }, [filterName]);
    
    // Handler per il save del filtro
    const handleSaveFilter = useCallback(async () => {
        if (!filterName || filterName.trim().length < 3) {
            // console.error('Nome filtro non valido');
            
            notify({
                message: 'Il nome del filtro deve essere di almeno 3 caratteri',
                type: 'error',
                displayTime: 3000,
                position: { at: 'top center', my: 'top center' }
            });
            
            return false;
        }
        
        if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) {
            console.error('Valore filtro non valido');
            
            notify({
                message: 'È necessario definire almeno una condizione di filtro',
                type: 'error',
                displayTime: 3000,
                position: { at: 'top center', my: 'top center' }
            });
            
            return false;
        }
        
        // Imposta lo stato di save avvenuto
        setIsSaving(true);
        
        try {
            const filtroUtenteDto = {
                filtroId: selectedFilter?.filtroId || undefined, // se esiste, stiamo aggiornando un filtro esistente
                nome: filterName.trim(),
                espressione: typeof filterValue === 'string' ? filterValue : JSON.stringify(filterValue)
            };
            
            // console.log('Salvataggio filtro:', filtroUtenteDto);
            
            let result;
            if (selectedFilter?.filtroId) {
                // Aggiornamento
                // console.log('Eseguo operazione di update per filtroId:', selectedFilter.filtroId);
                result = await filtriTenantDataSource.update(selectedFilter.filtroId, filtroUtenteDto);
            } else {
                // Inserimento
                // console.log('Eseguo operazione di insert');
                result = await filtriTenantDataSource.insert(filtroUtenteDto);
            }
            // console.log('Risultato del salvataggio/aggiornamento:', result);
            

            //todo: questa parte è brutta, da riscrivere

            // Ottieni l'ID del filtro salvato (potrebbe essere incluso nel risultato o nel DTO originale)
            let savedFilterId = null;
            if (result && result.filtroId) {
                savedFilterId = result.filtroId;
            } else if (selectedFilter?.filtroId) {
                savedFilterId = selectedFilter.filtroId;
            } else if (result && typeof result === 'object') {
                // Tenta di trovare un campo che potrebbe contenere l'ID
                const possibleIdFields = ['id', 'filtroId', 'ID', 'Id'];
                for (const field of possibleIdFields) {
                    if (result[field] !== undefined) {
                        savedFilterId = result[field];
                        break;
                    }
                }
            }
            
            // console.log('ID del filtro salvato:', savedFilterId);
            
            // Memorizza l'ID dell'ultimo filtro salvato
            if (savedFilterId) {
                setLastSavedFilterId(savedFilterId);
            }
            
            // Incrementa il contatore di refresh per forzare un aggiornamento dell'interfaccia
            setRefreshCounter(prev => prev + 1);
            
            // Forza l'aggiornamento della griglia dei filtri usando il dataSource
            await filtriTenantDataSource.load();
            
            // Utilizziamo il metodo refreshData se disponibile
            if (filtriTenantGridRef.current && typeof filtriTenantGridRef.current.refreshData === 'function') {
                // Aumentiamo il timeout per dare più tempo al server di processare la richiesta
                setTimeout(() => {
                    // console.log('Eseguo refreshData dopo il salvataggio');
                    filtriTenantGridRef.current.refreshData();
                }, 500);
            }
            
            // Mostra un messaggio di successo
            notify({
                message: selectedFilter ? 'Filtro aggiornato con successo' : 'Filtro salvato con successo',
                type: 'success',
                displayTime: 3000,
                position: { at: 'top center', my: 'top center' }
            });
            
            return true;
        } catch (error) {
            console.error('Errore durante il salvataggio del filtro:', error);
            
            // Mostra un messaggio di errore
            notify({
                message: 'Errore durante il salvataggio del filtro',
                type: 'error',
                displayTime: 3000,
                position: { at: 'top center', my: 'top center' }
            });
            
            return false;
        } finally {
            // Resetta lo stato di salvataggio
            setIsSaving(false);
        }
    }, [filtriTenantDataSource, selectedFilter, filterName, filterValue]);
    
    // Passa il riferimento alla griglia FiltriTenant
    const setFiltriTenantRef = useCallback((ref: any) => {
        // console.log('Riferimento alla griglia FiltriTenant ottenuto:', ref);
        filtriTenantGridRef.current = ref;
    }, []);

    // Gestore che viene chiamato quando il DataGrid ha completato l'inizializzazione 
    // e il rendering iniziale del contenuto
    const handleGridContentReady = useCallback((e: any) => {
        // console.log('DataGrid ha completato il rendering:', e);
        
        // Se abbiamo appena salvato un filtro, possiamo selezionarlo
        if (lastSavedFilterId !== null) {
            // console.log('Seleziona il filtro appena salvato:', lastSavedFilterId);
        }
    }, [lastSavedFilterId]);

    // Gestore degli errori del DataGrid
    const handleGridError = useCallback((e: any) => {
        //console.error('Errore nel DataGrid:', e);
        
        notify({
            message: `Errore: ${e.message || 'Si è verificato un errore nel caricamento dei dati'}`,
            type: 'error',
            displayTime: 3000,
            position: { at: 'top center', my: 'top center' }
        });
    }, []);

    return (
        <>
            <h2 className={'content-block'}>Elenco filtri</h2>
            <div className={'content-block'}>
                <div className={'dx-card responsive-paddings'}>
                    <ResponsiveBox singleColumnScreen="sm" >
                        <Row ratio={1} />
                        <Row ratio={1} />
                        <Row ratio={1} />
                        <Col ratio={2} screen="lg" />
                        <Col ratio={3} />
                        <Item>
                            <Location row={1} col={0} />
                            <FiltriTenant 
                                dataSource={filtriTenantDataSource}
                                onFilterSelect={handleFilterSelect}
                                onFilterDelete={handleFilterDelete}
                                selectedFilterId={selectedFilter?.filtroId || lastSavedFilterId || null}
                                componentRef={setFiltriTenantRef}
                                key={`filtri-tenant-${refreshCounter}`} // Forza un re-render quando cambia il contatore
                                // Passiamo i nuovi gestori degli eventi
                                onDataErrorOccurred={handleGridError}
                                onContentReady={handleGridContentReady}
                                refreshTrigger={refreshCounter}
                            />
                        </Item>
                        <Item>
                            <Location row={2} col={0} />
                            <Filtro 
                                fields={fields}
                                filterValue={filterValue}
                                filterName={filterName}
                                onFilterValueChanged={handleFilterValueChanged}
                                onFilterNameChanged={setFilterName}
                                onResetFilter={handleFilterReset}
                                onShowPreview={handleShowPreview}
                                onSaveFilter={handleSaveFilter}
                                isSaving={isSaving}
                            />
                        </Item>
                        <Item>
                            <Location row={0} col={1} rowspan={4} />
                            <div className="spazio-sinistro-anteprima-filtro">
                                <AnteprimaFiltro 
                                    dataSource={showPreview && selectedFilter ? anteprimaDataSource : null}
                                    showPreview={showPreview && !!selectedFilter}
                                />
                            </div>
                        </Item>
                    </ResponsiveBox>
                </div>
            </div>
        </>
    );
}


